// ═══════════════════════════════════════════════════════════
// Public Portal Layout — PSPSuiteBar visual style, simplified
// No hamburger, no OG Assist, no settings. Just the brand
// chrome + portal nav + user avatar.
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { IconChevronDown, IconLogout, IconUser, IconMenu2, IconX } from '@tabler/icons-react';
import { usePublicAuthStore } from '../store/publicAuthStore';
import { useJurisdiction } from '../contexts/JurisdictionContext';
import { usePortalRealtimeEvents } from '../hooks/usePortalRealtimeEvents';

const NAV_LINKS = [
  { label: 'Home',             to: '/portal',        exact: true },
  { label: 'Submit a Request', to: '/portal/submit', exact: false },
  { label: 'Track Status',     to: '/portal/track',  exact: false },
  { label: 'Records Library',  to: '/portal/library', exact: false },
];

export default function PortalLayout() {
  const publicUser = usePublicAuthStore(s => s.user);
  const logout     = usePublicAuthStore(s => s.logout);
  const jurisdiction = useJurisdiction();
  const navigate = useNavigate();
  usePortalRealtimeEvents();

  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = publicUser
    ? [publicUser.firstName?.[0], publicUser.lastName?.[0]].filter(Boolean).join('')
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--color-neutral-50)' }}>

      {/* ── Top bar ─────────────────────────────────────────── */}
      <header style={{
        height: 56, flexShrink: 0,
        background: 'white',
        borderBottom: '1px solid var(--color-neutral-200)',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: 16,
        position: 'sticky', top: 0, zIndex: 100,
      }}>

        {/* Wordmark + product name */}
        <Link
          to="/portal"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            textDecoration: 'none', flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <img
              src="/cds/assets/logo/OpenGov-Logo-Blurple.svg"
              alt="OpenGov"
              style={{ height: 18, display: 'block' }}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)', marginTop: 2, letterSpacing: 0.1 }}>
              OpenRecords
            </span>
          </div>
        </Link>

        {/* Nav links — hidden on mobile via CSS */}
        <nav className="portal-topbar-nav" style={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
          {NAV_LINKS.map(({ label, to, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) => `topbar-nav-item${isActive ? ' active' : ''}`}
              style={{ height: 56 }}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Mobile hamburger — shown only on mobile via CSS */}
        <button
          className="portal-mobile-btn"
          onClick={() => setMobileNavOpen(o => !o)}
          aria-label="Navigation menu"
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <IconX size={20} /> : <IconMenu2 size={20} />}
        </button>

        {/* Avatar / Sign In */}
        <div ref={accountRef} style={{ position: 'relative', flexShrink: 0, marginLeft: 8 }}>
          {publicUser && initials ? (
            <>
              <button
                onClick={() => setAccountOpen(o => !o)}
                aria-label={`Account: ${publicUser.firstName}`}
                aria-expanded={accountOpen}
                style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: 'var(--color-primary-700)', color: 'white',
                  fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
                  border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                {initials}
              </button>

              {accountOpen && (
                <div style={{
                  position: 'absolute', top: 42, right: 0, zIndex: 2000,
                  background: 'white', borderRadius: 4,
                  border: '1px solid var(--color-neutral-200)',
                  boxShadow: 'var(--shadow-dialog)',
                  minWidth: 200, overflow: 'hidden',
                }}>
                  <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--color-neutral-200)' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-1000)' }}>
                      {publicUser.firstName} {publicUser.lastName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 2 }}>
                      {publicUser.email}
                    </div>
                  </div>
                  <button
                    onClick={() => { setAccountOpen(false); navigate('/portal/account'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '10px 14px', border: 'none',
                      background: 'transparent', cursor: 'pointer', textAlign: 'left',
                      fontSize: 14, color: 'var(--color-neutral-900)', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-neutral-50)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <IconUser size={15} color="var(--color-neutral-700)" />
                    My Account
                  </button>
                  <button
                    onClick={() => { setAccountOpen(false); logout(); navigate('/portal'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '10px 14px', border: 'none',
                      background: 'transparent', cursor: 'pointer', textAlign: 'left',
                      fontSize: 14, color: 'var(--cds-red-600)', fontFamily: 'inherit',
                      borderTop: '1px solid var(--color-neutral-200)',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--cds-red-50)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <IconLogout size={15} />
                    Sign Out
                  </button>
                </div>
              )}
            </>
          ) : (
            <NavLink
              to="/portal/account"
              className={({ isActive }) => `topbar-nav-item${isActive ? ' active' : ''}`}
              style={{ height: 56, gap: 8 }}
            >
              <IconUser size={14} />
              Sign In
            </NavLink>
          )}
        </div>
      </header>

      {/* Mobile nav drawer — hidden on desktop via CSS */}
      {mobileNavOpen && (
        <>
          <div
            onClick={() => setMobileNavOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 98 }}
          />
          <nav className="portal-mobile-nav open" aria-label="Mobile navigation">
            {NAV_LINKS.map(({ label, to, exact }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                className={({ isActive }) => `portal-mobile-nav-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileNavOpen(false)}
              >
                {label}
              </NavLink>
            ))}
            {publicUser ? (
              <>
                <NavLink
                  to="/portal/account"
                  className={({ isActive }) => `portal-mobile-nav-link${isActive ? ' active' : ''}`}
                  onClick={() => setMobileNavOpen(false)}
                >
                  My Account
                </NavLink>
                <button
                  onClick={() => { setMobileNavOpen(false); logout(); navigate('/portal'); }}
                  className="portal-mobile-nav-link"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left', color: 'var(--color-error)' }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <NavLink
                to="/portal/account"
                className={({ isActive }) => `portal-mobile-nav-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileNavOpen(false)}
              >
                Sign In
              </NavLink>
            )}
          </nav>
        </>
      )}

      {/* ── Content ─────────────────────────────────────────── */}
      <main className="portal-main" style={{ flex: 1 }}>
        <Outlet />
      </main>

    </div>
  );
}
